import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Divider,
    List,
    ListItemButton,
    ListItemText,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { DateTime } from 'luxon';
import { useAuth } from '../../auth/auth-provider';
import { ILocationAdminDto, IPlanAdminDto, IStoredFileDto, ImageDto, LinkDto } from '../../clients/services';
import { MainContent } from '../../components/MainContent';
import { MediaPickerDialog } from '../AdminShared/MediaPickerDialog';
import { ConcertSeriesWorkbenchService } from '../../services/concert-series-workbench.service';

const service = new ConcertSeriesWorkbenchService();
const serviceUrl = (process.env.REACT_APP_SERVICE_URL as string | undefined)?.replace(/\/$/, '') ?? '';

const compactAdminFormSx = {
    '& .MuiTextField-root .MuiInputBase-input': {
        fontSize: '0.86rem',
        paddingTop: '8px',
        paddingBottom: '8px',
    },
    '& .MuiInputLabel-root': {
        fontSize: '0.82rem',
    },
    '& .MuiFormHelperText-root': {
        fontSize: '0.72rem',
    },
    '& .MuiButton-root': {
        fontSize: '0.8rem',
        paddingTop: '4px',
        paddingBottom: '4px',
        minHeight: 30,
    },
    '& .MuiTypography-h5': {
        fontSize: '1.2rem',
    },
    '& .MuiTypography-h6': {
        fontSize: '1rem',
    },
};

const sortLocationsByName = (items: ILocationAdminDto[]): ILocationAdminDto[] => {
    return [...items].sort((a, b) => (a.name || '').localeCompare(b.name || '', 'de-CH'));
};

const sortPlansByName = (items: IPlanAdminDto[]): IPlanAdminDto[] => {
    return [...items].sort((a, b) => (a.name || '').localeCompare(b.name || '', 'de-CH'));
};

const cloneLocation = (item: ILocationAdminDto): ILocationAdminDto => {
    return {
        ...item,
        links: (item.links ?? []).map((link) => new LinkDto(link)),
        images: [...(item.images ?? [])],
    };
};

const resolvePublicUrl = (file: IStoredFileDto): string => {
    const rawUrl = file.publicUrl ?? '';
    if (!rawUrl) {
        return '';
    }

    if (rawUrl.startsWith('http://') || rawUrl.startsWith('https://')) {
        return rawUrl;
    }

    const normalizedPath = rawUrl.startsWith('/') ? rawUrl : `/${rawUrl}`;
    if (!serviceUrl) {
        return normalizedPath;
    }

    return `${serviceUrl}${normalizedPath}`;
};

const toDateLabel = (value?: DateTime | null): string => {
    if (!value || typeof value.toFormat !== 'function') {
        return '-';
    }

    return value.toFormat('dd.MM.yyyy HH:mm');
};

const toErrorMessage = (error: unknown, fallback: string): string => {
    if (typeof error === 'string' && error.trim()) {
        return error;
    }

    if (error && typeof error === 'object' && 'message' in error) {
        const message = String((error as { message?: unknown }).message ?? '');
        if (message.trim()) {
            return message;
        }
    }

    return fallback;
};

export const LocationsWorkbench: React.FC = () => {
    const { profile, hasRole } = useAuth();
    const { locationId } = useParams();
    const navigate = useNavigate();
    const fileInputRef = useRef<HTMLInputElement | null>(null);

    const isAllowed = hasRole(profile, 'admin');

    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [isUploadingImage, setIsUploadingImage] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [searchText, setSearchText] = useState('');

    const [locations, setLocations] = useState<ILocationAdminDto[]>([]);
    const [plans, setPlans] = useState<IPlanAdminDto[]>([]);

    const [selectedLocationId, setSelectedLocationId] = useState<number | null>(null);
    const [draft, setDraft] = useState<ILocationAdminDto | null>(null);

    const [isMediaPickerOpen, setIsMediaPickerOpen] = useState(false);
    const [mediaFiles, setMediaFiles] = useState<IStoredFileDto[]>([]);

    const visibleLocations = useMemo(() => {
        const needle = searchText.trim().toLowerCase();
        if (!needle) {
            return locations;
        }

        return locations.filter((location) => {
            const parts = [location.name, location.city, location.street, location.zip]
                .map((value) => (value ?? '').toLowerCase())
                .join(' ');
            return parts.includes(needle);
        });
    }, [locations, searchText]);

    const selectedPlans = useMemo(() => {
        if (!draft) {
            return [] as IPlanAdminDto[];
        }

        return sortPlansByName(plans.filter((plan) => plan.locationId === draft.id));
    }, [draft, plans]);

    const loadAll = async () => {
        setIsLoading(true);
        setError('');
        try {
            const [loadedLocations, loadedPlans] = await Promise.all([
                service.loadLocationsAsync(),
                service.loadPlansAsync(),
            ]);
            setLocations(sortLocationsByName(loadedLocations));
            setPlans(sortPlansByName(loadedPlans));
        } catch (e) {
            setError(toErrorMessage(e, 'Konzertlokale und Sitzpläne konnten nicht geladen werden.'));
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (!isAllowed) {
            return;
        }

        loadAll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAllowed]);

    useEffect(() => {
        if (!locations.length) {
            setSelectedLocationId(null);
            setDraft(null);
            return;
        }

        const parsed = locationId ? Number.parseInt(locationId, 10) : NaN;
        if (Number.isInteger(parsed)) {
            const match = locations.find((location) => location.id === parsed);
            if (match) {
                setSelectedLocationId(match.id);
                setDraft(cloneLocation(match));
                return;
            }
        }

        const first = locations[0];
        setSelectedLocationId(first.id);
        setDraft(cloneLocation(first));
        navigate(`/admin/locations/${first.id}`, { replace: true });
    }, [locationId, locations, navigate]);

    const updateLocationInState = (updated: ILocationAdminDto) => {
        setLocations((current) =>
            sortLocationsByName(current.map((item) => (item.id === updated.id ? updated : item))),
        );
        setDraft(cloneLocation(updated));
    };

    const onSelectLocation = (id: number) => {
        navigate(`/admin/locations/${id}`);
    };

    const onCreateLocation = async () => {
        const name = window.prompt('Name des Konzertlokals', 'Neues Konzertlokal');
        if (!name || !name.trim()) {
            return;
        }

        setError('');
        try {
            const created = await service.createLocationAsync(name);
            setLocations((current) => sortLocationsByName([...current, created]));
            setSuccess('Konzertlokal erstellt.');
            navigate(`/admin/locations/${created.id}`);
        } catch (e) {
            setError(toErrorMessage(e, 'Konzertlokal konnte nicht erstellt werden.'));
        }
    };

    const onSaveLocation = async () => {
        if (!draft) {
            return;
        }

        if (!draft.name || !draft.name.trim()) {
            setError('Der Name des Konzertlokals ist erforderlich.');
            return;
        }

        setIsSaving(true);
        setError('');
        try {
            const updated = await service.updateLocationAsync(draft.id, draft);
            updateLocationInState(updated);
            setSuccess('Konzertlokal gespeichert.');
        } catch (e) {
            setError(toErrorMessage(e, 'Konzertlokal konnte nicht gespeichert werden.'));
        } finally {
            setIsSaving(false);
        }
    };

    const onDeleteLocation = async () => {
        if (!draft) {
            return;
        }

        const confirmed = window.confirm(`Konzertlokal "${draft.name}" wirklich loeschen?`);
        if (!confirmed) {
            return;
        }

        setError('');
        try {
            await service.deleteLocationAsync(draft.id);
            const remaining = locations.filter((item) => item.id !== draft.id);
            setLocations(remaining);
            setSuccess('Konzertlokal geloescht.');

            if (!remaining.length) {
                navigate('/admin/locations');
                return;
            }

            navigate(`/admin/locations/${remaining[0].id}`);
        } catch (e) {
            setError(
                toErrorMessage(
                    e,
                    'Konzertlokal konnte nicht geloescht werden. Das Konzertlokal wird eventuell noch verwendet.',
                ),
            );
        }
    };

    const onCreatePlan = async () => {
        if (!draft) {
            return;
        }

        const name = window.prompt('Name der Planvorlage', `${draft.name} Plan`);
        if (!name || !name.trim()) {
            return;
        }

        setError('');
        try {
            const created = await service.createPlanAsync(name, draft.id);
            setPlans((current) => sortPlansByName([...current, created]));
            setSuccess('Plan erstellt.');
            navigate(`/admin/plan-editor/${created.id}`);
        } catch (e) {
            setError(toErrorMessage(e, 'Plan konnte nicht erstellt werden.'));
        }
    };

    const onDeletePlan = async (plan: IPlanAdminDto) => {
        const confirmed = window.confirm(`Plan "${plan.name}" wirklich loeschen?`);
        if (!confirmed) {
            return;
        }

        setError('');
        try {
            await service.deletePlanAsync(plan.id);
            setPlans((current) => current.filter((item) => item.id !== plan.id));
            setSuccess('Plan geloescht.');
        } catch (e) {
            setError(toErrorMessage(e, 'Plan konnte nicht geloescht werden. Der Plan wird eventuell noch verwendet.'));
        }
    };

    const addLocationImage = async (image: ImageDto) => {
        if (!draft) {
            return;
        }

        setError('');
        try {
            const updated = await service.addLocationImageAsync(draft.id, image);
            updateLocationInState(updated);
            setSuccess('Bild hinzugefuegt.');
        } catch (e) {
            setError(toErrorMessage(e, 'Bild konnte nicht hinzugefuegt werden.'));
        }
    };

    const onUploadLocationImage = async (file: File) => {
        setIsUploadingImage(true);
        try {
            const uploaded = await service.uploadMediaImageAsync(file);
            await addLocationImage(
                new ImageDto({
                    url: resolvePublicUrl(uploaded),
                    title: uploaded.title || uploaded.fileName,
                }),
            );
        } finally {
            setIsUploadingImage(false);
        }
    };

    const onPickLocationImage = async () => {
        setError('');
        try {
            await refreshMediaFiles();
            setIsMediaPickerOpen(true);
        } catch (e) {
            setError(toErrorMessage(e, 'Medienbibliothek konnte nicht geladen werden.'));
        }
    };

    const refreshMediaFiles = async () => {
        const files = await service.listMediaImagesAsync();
        setMediaFiles(files);
    };

    const uploadMediaFromDialog = async (file: File): Promise<boolean> => {
        try {
            await service.uploadMediaImageAsync(file);
            setSuccess('Datei hochgeladen.');
            return true;
        } catch (e) {
            setError(toErrorMessage(e, 'Upload fehlgeschlagen.'));
            return false;
        }
    };

    const onRemoveLocationImage = async (url: string) => {
        if (!draft) {
            return;
        }

        setError('');
        try {
            const updated = await service.removeLocationImageAsync(draft.id, url);
            updateLocationInState(updated);
            setSuccess('Bild entfernt.');
        } catch (e) {
            setError(toErrorMessage(e, 'Bild konnte nicht entfernt werden.'));
        }
    };

    if (!isAllowed) {
        return (
            <MainContent>
                <Alert severity="warning">Kein Zugriff.</Alert>
            </MainContent>
        );
    }

    if (isLoading) {
        return (
            <MainContent>
                <CircularProgress />
            </MainContent>
        );
    }

    return (
        <MainContent>
            <Stack
                direction="row"
                sx={{
                    height: 'calc(100vh - 220px)',
                    border: '1px solid',
                    borderColor: 'divider',
                    backgroundColor: '#ece8df',
                    color: '#1f1f1f',
                }}
            >
                <Box
                    sx={{
                        width: 360,
                        borderRight: '1px solid',
                        borderColor: 'divider',
                        p: 1.25,
                        overflow: 'auto',
                        ...compactAdminFormSx,
                    }}
                >
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                        <Typography variant="h6">Konzertlokale</Typography>
                        <Button variant="contained" size="small" onClick={onCreateLocation}>
                            Neu
                        </Button>
                    </Stack>

                    <TextField
                        fullWidth
                        size="small"
                        label="Suche"
                        value={searchText}
                        onChange={(event) => setSearchText(event.target.value)}
                        sx={{ mb: 1 }}
                    />

                    <List dense>
                        {visibleLocations.map((location) => (
                            <ListItemButton
                                key={location.id}
                                selected={selectedLocationId === location.id}
                                onClick={() => onSelectLocation(location.id)}
                            >
                                <ListItemText
                                    primary={location.name || 'Ohne Name'}
                                    secondary={location.city || location.street || '-'}
                                />
                            </ListItemButton>
                        ))}
                    </List>
                </Box>

                <Box sx={{ flex: 1, p: 1.25, overflow: 'auto', backgroundColor: '#fffbf2', ...compactAdminFormSx }}>
                    {!draft && <Alert severity="info">Bitte wähle ein Konzertlokal aus.</Alert>}

                    {draft && (
                        <Stack spacing={1.2}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                <Typography variant="h5">Konzertlokal bearbeiten</Typography>
                                <Stack direction="row" spacing={1}>
                                    <Button variant="outlined" color="error" onClick={onDeleteLocation}>
                                        Löschen
                                    </Button>
                                    <Button variant="contained" onClick={onSaveLocation} disabled={isSaving}>
                                        Speichern
                                    </Button>
                                </Stack>
                            </Stack>

                            <TextField
                                label="Name"
                                fullWidth
                                value={draft.name || ''}
                                onChange={(event) => setDraft({ ...draft, name: event.target.value })}
                            />

                            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
                                <TextField
                                    label="Strasse"
                                    fullWidth
                                    value={draft.street ?? ''}
                                    onChange={(event) => setDraft({ ...draft, street: event.target.value })}
                                />
                                <TextField
                                    label="PLZ"
                                    fullWidth
                                    value={draft.zip ?? ''}
                                    onChange={(event) => setDraft({ ...draft, zip: event.target.value })}
                                />
                                <TextField
                                    label="Ort"
                                    fullWidth
                                    value={draft.city ?? ''}
                                    onChange={(event) => setDraft({ ...draft, city: event.target.value })}
                                />
                            </Stack>

                            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
                                <TextField
                                    label="Land"
                                    fullWidth
                                    value={draft.country ?? ''}
                                    onChange={(event) => setDraft({ ...draft, country: event.target.value })}
                                />
                                <TextField
                                    label="Telefon"
                                    fullWidth
                                    value={draft.phone ?? ''}
                                    onChange={(event) => setDraft({ ...draft, phone: event.target.value })}
                                />
                            </Stack>

                            <TextField
                                label="Beschreibung"
                                fullWidth
                                multiline
                                minRows={2}
                                value={draft.description ?? ''}
                                onChange={(event) => setDraft({ ...draft, description: event.target.value })}
                            />

                            <TextField
                                label="Kommentar"
                                fullWidth
                                multiline
                                minRows={2}
                                value={draft.comment ?? ''}
                                onChange={(event) => setDraft({ ...draft, comment: event.target.value })}
                            />

                            <Divider sx={{ my: 1 }} />
                            <Typography variant="h6">Links</Typography>
                            {(draft.links ?? []).map((link, index) => (
                                <Stack key={`${link.url}-${index}`} direction={{ xs: 'column', md: 'row' }} spacing={1}>
                                    <TextField
                                        label="Titel"
                                        fullWidth
                                        value={link.title ?? ''}
                                        onChange={(event) => {
                                            const next = [...(draft.links ?? [])];
                                            next[index] = new LinkDto({ ...next[index], title: event.target.value });
                                            setDraft({ ...draft, links: next });
                                        }}
                                    />
                                    <TextField
                                        label="URL"
                                        fullWidth
                                        value={link.url ?? ''}
                                        onChange={(event) => {
                                            const next = [...(draft.links ?? [])];
                                            next[index] = new LinkDto({ ...next[index], url: event.target.value });
                                            setDraft({ ...draft, links: next });
                                        }}
                                    />
                                    <Button
                                        color="error"
                                        onClick={() => {
                                            const next = [...(draft.links ?? [])];
                                            next.splice(index, 1);
                                            setDraft({ ...draft, links: next });
                                        }}
                                    >
                                        X
                                    </Button>
                                </Stack>
                            ))}
                            <Button
                                variant="outlined"
                                onClick={() =>
                                    setDraft({
                                        ...draft,
                                        links: [...(draft.links ?? []), new LinkDto({ title: '', url: '' })],
                                    })
                                }
                            >
                                Link hinzufuegen
                            </Button>

                            <Divider sx={{ my: 1 }} />
                            <Typography variant="h6">Bilder</Typography>
                            <Stack direction="row" spacing={1}>
                                <Button
                                    variant="outlined"
                                    onClick={() => fileInputRef.current?.click()}
                                    disabled={isUploadingImage}
                                >
                                    Datei hochladen
                                </Button>
                                <Button variant="outlined" onClick={onPickLocationImage} disabled={isUploadingImage}>
                                    Medienbibliothek
                                </Button>
                            </Stack>

                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/*"
                                hidden
                                onChange={async (event) => {
                                    const file = event.target.files?.[0];
                                    if (file) {
                                        await onUploadLocationImage(file);
                                    }
                                    event.currentTarget.value = '';
                                }}
                            />

                            <Stack spacing={1}>
                                {(draft.images ?? []).map((image, index) => (
                                    <Stack
                                        key={`${image.url}-${index}`}
                                        direction={{ xs: 'column', md: 'row' }}
                                        spacing={1}
                                        alignItems={{ xs: 'stretch', md: 'center' }}
                                        sx={{ border: '1px solid', borderColor: 'divider', p: 1, borderRadius: 1 }}
                                    >
                                        <Box
                                            component="img"
                                            src={image.url}
                                            alt={image.title || 'Bild'}
                                            sx={{ width: 120, height: 80, objectFit: 'cover', borderRadius: 1 }}
                                        />
                                        <Box sx={{ flex: 1 }}>
                                            <Typography>{image.title || 'Ohne Titel'}</Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {image.url}
                                            </Typography>
                                        </Box>
                                        <Button color="error" onClick={() => onRemoveLocationImage(image.url || '')}>
                                            Entfernen
                                        </Button>
                                    </Stack>
                                ))}
                            </Stack>

                            <Divider sx={{ my: 1 }} />
                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                <Typography variant="h6">Sitzpläne</Typography>
                                <Button variant="contained" size="small" onClick={onCreatePlan}>
                                    Plan anlegen
                                </Button>
                            </Stack>

                            <Stack spacing={0.5}>
                                {selectedPlans.map((plan) => (
                                    <Stack
                                        key={plan.id}
                                        direction={{ xs: 'column', md: 'row' }}
                                        spacing={1}
                                        justifyContent="space-between"
                                        alignItems={{ xs: 'flex-start', md: 'center' }}
                                        sx={{ border: '1px solid', borderColor: 'divider', p: 1, borderRadius: 1 }}
                                    >
                                        <Box>
                                            <Button
                                                component={RouterLink}
                                                to={`/admin/plan-editor/${plan.id}`}
                                                variant="text"
                                                sx={{ px: 0, minWidth: 0 }}
                                            >
                                                {plan.name}
                                            </Button>
                                            <Typography variant="body2" color="text.secondary">
                                                Letzte Aenderung: {toDateLabel(plan.dateChanged ?? plan.dateCreated)}
                                            </Typography>
                                        </Box>
                                        <Button color="error" size="small" onClick={() => onDeletePlan(plan)}>
                                            Loeschen
                                        </Button>
                                    </Stack>
                                ))}
                                {selectedPlans.length === 0 && (
                                    <Typography variant="body2" color="text.secondary">
                                        Kein Plan für dieses Konzertlokal vorhanden.
                                    </Typography>
                                )}
                            </Stack>
                        </Stack>
                    )}

                    {error && (
                        <Alert sx={{ mt: 2 }} severity="error" onClose={() => setError('')}>
                            {error}
                        </Alert>
                    )}

                    {success && (
                        <Alert sx={{ mt: 2 }} severity="success" onClose={() => setSuccess('')}>
                            {success}
                        </Alert>
                    )}
                </Box>
            </Stack>

            <MediaPickerDialog
                open={isMediaPickerOpen}
                files={mediaFiles}
                onClose={() => setIsMediaPickerOpen(false)}
                onUpload={uploadMediaFromDialog}
                onRefresh={refreshMediaFiles}
                onPick={async (file) => {
                    await addLocationImage(
                        new ImageDto({
                            url: resolvePublicUrl(file),
                            title: file.title || file.fileName,
                        }),
                    );
                }}
            />
        </MainContent>
    );
};
